import React, { useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import NunitoText from "../general/NunitoText";
import ReusableAutocompleteWithID from "../general/ReusableAutocompleteWithID";
import ReusableButton from "../general/ReusableButton";
import ReusableDatePicker from "../general/ReusableDatePicker";
import ReusableTextField from "../general/ReusableTextField";
import { showWarningSwal } from "../../functions/alert";
import { gradeOptions } from "../../functions/general";
import {
  getAssignment,
  getAssignmentStoreFilterOptions,
} from "../../functions/getData";
import {
  extractAssignmentFilterOptions,
  toAssignmentSelectOption,
} from "../../functions/assignmentFilterOptions";
import { updateAssignment } from "../../functions/patchData";

const rowContainerStyle = { paddingInline: 34, marginBottom: 24 };
const buttonsContainerStyle = { paddingInline: 100, marginBlock: 30 };
const boxStyle = { width: 1100, maxHeight: "90%" };

const toDateString = (timestamp) => {
  if (!timestamp) return null;
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const toTimestamp = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.setHours(0, 0, 0, 0);
};

export default function EditAssigneeModal({
  id,
  row,
  setLoading,
  afterEdit,
  cancelEdit,
}) {
  const [loadingData, setLoadingData] = useState(true);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [grades, setGrades] = useState([]);
  const [dateRange, setDateRange] = useState(null);

  const competitionLabel = row?.competition || "";
  const countryLabel = row?.country || "";
  const organizationLabel = row?.organization || "";

  useEffect(() => {
    if (!id) return;
    setLoadingData(true);
    getAssignment(id)
      .then((res) => {
        const body = res?.data ?? res ?? {};
        // Pivot rows look like `{ id, language_id, progress, ..., language }` so the
        // real language id lives on `language_id` (or `language.id`), not `id`.
        const langIds = Array.isArray(body.languages)
          ? body.languages
              .map((l) => {
                if (l === null || l === undefined) return null;
                if (typeof l === "number" || typeof l === "string") return l;
                return l.language_id ?? l.language?.id ?? l.id ?? null;
              })
              .filter((v) => v !== null && v !== undefined)
          : [];
        const gradeIds = Array.isArray(body.grades)
          ? body.grades
              .map((g) => {
                if (g === null || g === undefined) return null;
                if (typeof g === "number" || typeof g === "string") return g;
                return g.grade_id ?? g.grade?.id ?? g.id ?? null;
              })
              .filter((v) => v !== null && v !== undefined)
          : [];
        const fromTs = toTimestamp(body.from_date || body.from);
        const toTs = toTimestamp(body.to_date || body.to);
        setLanguages(langIds);
        setGrades(gradeIds);
        setDateRange(fromTs && toTs ? [fromTs, toTs] : null);

        const competitionId = body.competition_id ?? body.competition?.id;
        const countryId = body.country_id ?? body.country?.id;
        const orgId = body.organization_id ?? body.organization?.id;
        if (competitionId && countryId && orgId) {
          getAssignmentStoreFilterOptions(
            `?get_filter=languages&competition=${competitionId}&country=${countryId}&organization=${orgId}`
          )
            .then((r) =>
              setLanguageOptions(
                extractAssignmentFilterOptions(r, [
                  "languages",
                  "language",
                ]).map(toAssignmentSelectOption)
              )
            )
            .catch(() => setLanguageOptions([]));
        }
      })
      .catch(() => {})
      .finally(() => setLoadingData(false));
  }, [id]);

  const onSubmit = () => {
    let warning = "";
    if (!languages.length)
      warning += "Please select at least one language.<br>";
    if (!grades.length) warning += "Please select at least one grade.<br>";
    if (!dateRange || !Array.isArray(dateRange) || dateRange.length < 2)
      warning += "Please pick the From and To dates.<br>";
    if (warning) return showWarningSwal(warning);

    const payload = {
      languages,
      grades,
      from_date: toDateString(dateRange[0]),
      to_date: toDateString(dateRange[1]),
    };
    setLoading(true);
    updateAssignment(id, payload)
      .then((d) => {
        setLoading(false);
        afterEdit(cancelEdit, d);
      })
      .catch(() => setLoading(false));
  };

  return (
    <Box className="popUpModal horizontalScrollable" style={boxStyle}>
      <NunitoText
        value="EDIT ASSIGNEE"
        fontSize={30}
        fontWeight={600}
        color="#5E75C3"
        marginTop={20}
      />

      <Grid container justifyContent="space-between" style={rowContainerStyle}>
        <ReusableTextField
          type="withLabel"
          width={500}
          height={60}
          bgColor="#F2F2F2"
          placeholder="Competition"
          state={competitionLabel}
          setState={() => {}}
          readOnly
          grayedOut
        />
        <ReusableTextField
          type="withLabel"
          width={500}
          height={60}
          bgColor="#F2F2F2"
          placeholder="Country"
          state={countryLabel}
          setState={() => {}}
          readOnly
          grayedOut
        />
      </Grid>

      <Grid container style={rowContainerStyle}>
        <ReusableTextField
          type="withLabel"
          width={1030}
          height={60}
          bgColor="#F2F2F2"
          placeholder="Organization"
          state={organizationLabel}
          setState={() => {}}
          readOnly
          grayedOut
        />
      </Grid>

      <Grid container justifyContent="space-between" style={rowContainerStyle}>
        <ReusableAutocompleteWithID
          type="withLabel"
          width={500}
          placeholder="Languages*"
          state={languages}
          setState={setLanguages}
          required
          multiple
          options={languageOptions}
        />
        <ReusableAutocompleteWithID
          type="withLabel"
          width={500}
          placeholder="Grades*"
          state={grades}
          setState={setGrades}
          required
          multiple
          options={gradeOptions.map((g) => ({ id: g.id, option: g.name }))}
        />
      </Grid>

      <Grid container style={rowContainerStyle}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <span
            style={{
              color: "#000",
              fontSize: 16,
              fontFamily: "Nunito",
              fontWeight: 600,
              fontStyle: "italic",
              marginBottom: 4,
            }}
          >
            From - To*
          </span>
          <ReusableDatePicker
            width={500}
            range
            state={dateRange}
            setState={setDateRange}
            required={!dateRange}
          />
        </div>
      </Grid>

      <Grid container justifyContent="space-evenly" style={buttonsContainerStyle}>
        <ReusableButton
          text="Cancel"
          bgColor="#8D8D8D"
          fontSize={20}
          height={56}
          width={260}
          onClick={cancelEdit}
        />
        <ReusableButton
          text={loadingData ? "Loading..." : "Save"}
          bgColor="#5E75C3"
          fontSize={20}
          height={56}
          width={260}
          onClick={onSubmit}
          disabledButton={loadingData}
        />
      </Grid>
    </Box>
  );
}
